"use client";
import FormSelector from "@/Components/Form/FormSelector";
import HeadNav from "@/Components/Layout/Header";
import Sidenav from "@/Components/Layout/Sider";
import CustomModal from "@/Components/Modal";
import { formatColorCode, setThemeStyles } from "@/Utils/helpers";
import Loading from "@/app/loading";
import Login from "@/app/login/page";
import ResetPassword from "@/app/reset-password/index";
import { SearchProvider } from "@/context/SearchContext";
import TokenRefresher from "@/hooks/useRefreshToken";
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Avatar, Button, ConfigProvider, Form, Layout, Tooltip } from "antd";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import get from "lodash/get";
import { usePathname, useRouter } from "next/navigation";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import { useFetchUser } from "@/hooks/userHook";
import Img from "@/app/Img";
import ClosedBetaModal from "@/Components/ClosedBetaModal";
import { bootstrapApp, updateLanguageLocale } from "@/ServerActions";
import { getDeviceLanguage, getSelectedLanguage, setSelectedLanguage } from "@/Utils/languageManager";
import { JsonRecord, Locals } from "@/types";
import { useForm } from "antd/es/form/Form";
import logo from "@/Assets/Images/logo-blue.svg";
import { getDeviceId } from "@/Utils/deviceId";
import { getTitleLanguage } from "@/Utils/orderHelpers";
import { DirectionType } from "antd/es/config-provider";
import UserPlaceholder from "@/Assets/Images/userplaceholder.png"
import { getClientTimezone } from "@/Utils/TimezoneInitializer";

interface LanguageContextProps {
  language: string;
  languageObj: JsonRecord;
  handleLanguageChange: (value: string) => Promise<void>;
  langOption: Array<{ label: string; value: string }>;
  direction: string;
  isPending: boolean;
  defaultLanguage: any;
}

interface ThemeContextProps {
  theme: any;
  setTheme: (theme: any) => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);
const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const { Sider } = Layout;
interface ProtectedProps {
  children: React.ReactNode;
}

const ProtectedRoutes: React.FC<ProtectedProps> = ({
  children,
}) => {
  const [form] = useForm();
  const router = useRouter();
  const pathName = usePathname();

  const bootstrapCalled = useRef(false);
  const vendorLoaded = useRef(false);
  const isAuthenticated = Boolean(getCookie("accessTokenDot"));

  // social
  const { data: userRes, isLoading: isUserLoading } = useFetchUser(isAuthenticated);

  const defaultLanguage = getSelectedLanguage();
  const themeCookie = getCookie("themeDot");
  const defaultTheme = themeCookie ? JSON.parse(themeCookie) : {};

  const [isLoading, setIsLoading] = useState(true);
  const [isPending, SetIsPending] = useState(false);
  const [isClosedBetaOpen, setIsClosedBetaOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [theme, setTheme] = useState<any>(defaultTheme);
  const [language, setLanguage] = useState<string>(defaultLanguage);
  const [languageList, setLanguageList] = useState<any>([]);
  const [translationMap, setTranslationMap] = useState<Record<string, any>>({});


  const hasAuthenticated = Boolean(getCookie("accessTokenDot"));
  const hasLoginPagePath = Boolean(pathName === "/login");
  const hasResetPagePath = Boolean(pathName === "/reset-password");

  const userInFoDetails: any = get(userRes, "data.data.social", "");
  const isUserReady = !isUserLoading;
  const apiLocale = get(userRes, "data.data.locale", false);
  const storedLocale = getSelectedLanguage();

  const isLocale = apiLocale || storedLocale;
  const fullName = `${userInFoDetails?.first_name || ""} ${userInFoDetails?.last_name || ""}`.trim();
  const profilePicUrl = userInFoDetails?.profile_pic_url || UserPlaceholder.src;
  const brandLogo = get(theme, "brand_logo", logo);
  const isHideTitle = theme.logo_hide_title;

  const handleLanguageChange = async (value: string) => {
    setLanguage(value);
    SetIsPending(true);

    try {
      await updateLanguageLocale(value);

      // update cookie instantly
      setSelectedLanguage(value);
    } catch (error) {
      console.log(error);
    } finally {
      SetIsPending(false);
    }
  };

  const fetchThemeBootStrap = async () => {
    try {
      const resp = await bootstrapApp();

      const theme = get(resp, "data.theme", null);
      const languagelistGet = get(resp, "data.locales", null);
      const strings = get(resp, "data.strings", {});

      if (resp && theme) {
        setTheme(theme);
        setCookie("themeDot", theme);
        setLanguageList(languagelistGet);
        setTranslationMap(strings);
      }

    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const t = (map?: Record<string, string>) => getTitleLanguage(map, language);


  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const langOption = languageList?.map((lang: Locals) => ({
    label: lang.label,
    value: lang.locale,
  }));

  const rawDirection: DirectionType =
    languageList?.find((lang: Locals) => lang.locale === language)?.direction;

  const direction: DirectionType = rawDirection === "rtl" ? "rtl" : "ltr";
  const isRTL = direction === "rtl";

  const languageObj = React.useMemo(() => {
    if (!translationMap) return {};

    const obj: Record<string, string> = {};

    for (const key in translationMap) {
      obj[key] = getTitleLanguage(translationMap[key], language);
    }

    return obj;
  }, [translationMap, language]);

  //API CALLING USEEFFECT 
  useEffect(() => {
    if (userRes?.data?.data) {
      setCookie("userIdDot", userRes.data.data.id)
    }
  }, [userRes])

  useEffect(() => {
    const locale = get(userRes, "data.data.locale") || defaultLanguage;

    if (locale) {
      setLanguage(locale);
      setSelectedLanguage(locale);
    }
  }, [userRes]);

  useEffect(() => {
    setThemeStyles(theme);
  }, [theme]);

  useEffect(() => {
    if (bootstrapCalled.current) return;

    bootstrapCalled.current = true;
    fetchThemeBootStrap();
  }, []);

  useEffect(() => {
    getSelectedLanguage();
    getDeviceLanguage();
    getDeviceId();
    getClientTimezone();
  }, []);

  useEffect(() => {
    const check418 = () => {
      const betaFlag = getCookie("app418DotBeta");
      if (betaFlag === "true") {
        setIsClosedBetaOpen(true);
      }
    };

    check418();

    const interval = setInterval(check418, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!hasAuthenticated) {
      if (
        pathName !== "/login" &&
        pathName !== "/reset-password"
      ) {
        router.replace("/login");
      }
      return;
    }

    if (!hasAuthenticated && pathName === "/?tab=map_view") {
      router.replace("/login");
    }
    if (!hasAuthenticated && pathName === "/") {
      router.replace("/login");
    }
    if (hasAuthenticated && pathName === "/login") {
      router.replace("/");
    }
  }, [pathName, hasAuthenticated]);

  // Handle user logout
  const handleLogout = () => {
    deleteCookie("accessTokenDot");
    deleteCookie("refreshTokenDot");
    deleteCookie("userIdDot");
    deleteCookie("vendorIdDot");
    deleteCookie("expiresInDot");
    deleteCookie("serviceIdDot");
    deleteCookie("app418DotBeta");
    localStorage.removeItem("dateToggleState");
    router.replace("/login");
    toast.success("Logged out successfully, please login to continue!");
  };

  return (
    <>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <LanguageContext.Provider
          value={{
            language,
            languageObj,
            handleLanguageChange,
            langOption,
            direction,
            isPending,
            defaultLanguage,
          }}
        >
          <ConfigProvider
            direction={direction}
            theme={{
              components: {
                DatePicker: {
                  cellActiveWithRangeBg: "#D3D3D3",
                },
                Select: {
                  optionSelectedBg: "#D3D3D3",
                },
                Spin: {
                  colorBgBase: "#001d52"
                }
              },
              token: {
                colorPrimary: formatColorCode(theme.primary),
                colorBgBase: "#ffffff",
                colorBorder: formatColorCode(theme.primaryLight),
                colorError: formatColorCode(theme.error),
                colorSuccess: formatColorCode(theme.success),
                colorTextBase: formatColorCode(theme.primary),
              },
            }}
          >
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {hasLoginPagePath && <Login />}
                {hasResetPagePath && <ResetPassword />}
                {!hasLoginPagePath && !hasResetPagePath && (
                  <>
                    {isLocale ? (
                      <>
                        <SearchProvider>
                          <Layout className={styles.layout} dir={direction}>
                            <Sider
                              width={200}
                              theme="light"
                              breakpoint="md"
                              style={{
                                height: "100%",
                                position: "fixed",
                                top: "0",
                                // left: "0",
                                left: direction === "rtl" ? "auto" : "0",
                                right: direction === "rtl" ? "0" : "auto",
                                zIndex: "1000",
                              }}
                            >
                              <div className={styles.brandLogo}
                                style={{
                                  flexDirection: direction === "rtl" ? "row-reverse" : "row",
                                }}>
                                <Img
                                  src={brandLogo}
                                  width={38}
                                  height={38}
                                  alt="logo"
                                />
                                {!isHideTitle && (
                                  <h1 className={styles.brandName}>DoT</h1>
                                )}
                              </div>
                              <Sidenav />

                              <div className={styles.profileSection}>
                                <span style={{ display: "flex", alignItems: "center", flexDirection: direction === "rtl" ? "row-reverse" : "row", }}>
                                  <Avatar
                                    src={profilePicUrl}
                                    style={{ backgroundColor: "#686e7e" }}
                                    size={45}
                                    icon={<UserOutlined />}
                                  />
                                  <h4 className={styles.userFullName}>{fullName}</h4>
                                </span>
                                <Tooltip placement="top" title="Logout">
                                  <Button
                                    type="primary"
                                    icon={
                                      <LogoutOutlined
                                        style={{ fontWeight: "bolder" }}
                                      />
                                    }
                                    onClick={openModal}
                                    dir={direction}
                                  />
                                </Tooltip>
                              </div>
                            </Sider>
                            <Layout
                              style={{
                                backgroundColor: formatColorCode(theme.background),
                                width: "100%",
                                height: "100%",
                                position: "fixed",
                                overflow: "hidden",
                                margin: "0px",
                                padding: "0px",
                              }}
                            >
                              <HeadNav />
                              <div className={styles.contentContainer}>{children}</div>
                            </Layout>
                          </Layout>
                        </SearchProvider>
                      </>
                    ) : (
                      <CustomModal
                        title={languageObj?.please_select_language || "Please Select Language"}
                        confirmLoading={isPending}
                        open={!Boolean(isLocale)}
                        width={500}
                        cancelButtonProps={{ style: { display: "none" } }}
                        okText={languageObj?.submit || "Submit"}
                        onOk={() => form.submit()}
                      >
                        <Form
                          form={form}
                          onFinish={async () => {
                            const locale = form.getFieldValue("locale");
                            await handleLanguageChange(locale);
                            // ✅ navigate ONLY here
                            router.replace("/?tab=map_view");
                          }}
                        >
                          <Form.Item name="locale">
                            <FormSelector title="Locale" options={langOption} />
                          </Form.Item>
                        </Form>
                      </CustomModal>
                    )}
                    <TokenRefresher hasAuthenticated={hasAuthenticated} />
                    <ReactQueryDevtools initialIsOpen={false} />
                    <ClosedBetaModal
                      open={isClosedBetaOpen}
                      onClose={handleLogout}
                    />
                  </>
                )}
              </>
            )}

            <CustomModal
              title={languageObj?.confirmation}
              open={isModalOpen}
              onCancel={closeModal}
              onOk={() => {
                handleLogout();
                closeModal();
              }}
              okText={languageObj?.logout}
              cancelText={languageObj?.cancel}
              confirmLoading={false}
              width={450}

              // 👇 ADD THESE
              closable={true}
              closeIcon={<span style={{ fontSize: 30, }}>×</span>}
            >

              <p style={{ color: "black" }}>{languageObj?.are_you_sure_you_want_to_logout}</p>
            </CustomModal>
          </ConfigProvider>
        </LanguageContext.Provider>
      </ThemeContext.Provider>
    </>
  );
};

export default ProtectedRoutes;


export const useDictionary = (): LanguageContextProps => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useDictionary must be used within a LanguageProvider");
  }
  return context;
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};